<!-- eslint-disable camelcase -->
<script setup lang="ts">
import {
  computed, ref, watchEffect, onMounted, nextTick,
} from 'vue';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import AutoComplete, { AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent } from 'primevue/autocomplete';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputMask from 'primevue/inputmask';
import InputIcon from 'primevue/inputicon';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import {
  useAccounts,
  useIndustryCodes,
  useMarkets,
  useOpportunities,
  useStringFunctions,
} from '@apparatix/composables';
import { AccountTypes, AssociationAccount } from '@apparatix/types/Leads';
import { Address } from '@apparatix/types/Contacts';
import {
  Column,
  Columns,
  InputGroup,
  Stack,
  Toolbar,
  Typography,
} from './core';
import { newAccountSchema, PendingAccount, transformSearchResult } from '@apparatix/schemas/Account';
import { industrySchema } from '@apparatix/composables';

interface CreateAccountProps {
  isLoading: boolean;
  allowedCreationTypes: AccountTypes[];
  allowedSelectionTypes: AccountTypes[];
  disabled?: boolean;
  useAdvertiserSearch?: boolean;
  agencyId?: number;
  forceCreationType?: AccountTypes;
  defaultType?: AccountTypes;
  title?: string;
  hideTitle?: boolean;
  submitButtonLabel?: string;
  disableAccountSearch?: boolean;
  externallyManageAccountLimits?: boolean;
  showCancelButton?: boolean;
  industryCodeRequired?: boolean;
  allowAgencyInWizard: boolean;
  disallowAccountsWithAgencies?: boolean;
}

const props = defineProps<CreateAccountProps>();

const model = defineModel<PendingAccount>();

const emit = defineEmits<{
  (e: 'on:submit', value: PendingAccount): void;
  (e: 'on:name-change', value: string): void;
  (e: 'on:address-change', value: string): void;
  (e: 'on:account-selected', value: PendingAccount);
  (e: 'cancel'): void;
}>();

const { isEmptyString } = useStringFunctions();
const { getAdvertisersByAgency, searchAccounts } = useAccounts();
const { isLoading: isMarketsLoading, markets, placeholder: marketsPlaceholder } = useMarkets();
const {
  isLoading: isIndustryCodesLoading,
  industryCodes,
  placeholder: industryCodePlaceholder,
} = useIndustryCodes();
const { findUserById } = useOpportunities();

const validationSchema = props.industryCodeRequired
  ? newAccountSchema.merge(z.object({
    industry: industrySchema.required(),
  }))
  : newAccountSchema;


const {
  defineField,
  errors,
  handleSubmit,
  meta,
  resetForm,
  values,
  setValues,
} = useForm({
  validationSchema: toTypedSchema(validationSchema),
  initialValues: {
    type: props.defaultType ?? 'Advertiser',
  },
});

const [company] = defineField('name');
const [industry] = defineField('industry');
const [selectedMarkets] = defineField('markets');
const [type, typeAttrs] = defineField('type');
const [address1, address1Attrs] = defineField('addresses.0.addressLine1');
const [address2, address2Attrs] = defineField('addresses.0.addressLine2');
const [city, cityAttrs] = defineField('addresses.0.city');
const [state, stateAttrs] = defineField('addresses.0.region');
const [zip, zipAttrs] = defineField('addresses.0.postalCode');
const [phone, phoneAttrs] = defineField('phoneNumbers.0.number');

const accountSearchResults = ref<AssociationAccount[]>([]);
const selectedAccount = ref<AssociationAccount | string>();

const disableTypeSelection = computed(() => !Array.isArray(props.allowedCreationTypes) || props.allowedCreationTypes.length <= 1);

const hideTypeSelection = computed(() => disableTypeSelection.value && props.forceCreationType || !props.allowAgencyInWizard);

const canAddMoreAccounts = computed(() => {
  if (props.externallyManageAccountLimits) return true;
  return props.allowedCreationTypes.length > 0 || props.allowedSelectionTypes.length > 0;
});

watchEffect(() => {
  if (model?.value) {
    setValues(model.value);
  }
});

const disableSubmit = computed(() => {
  if (props.isLoading || props.disabled) return true;
  return !meta.value.valid;
});

const submitLabel = computed(() => {
  if (props.isLoading) {
    return 'Saving...';
  }

  return props.submitButtonLabel ?? 'Add Account';
});

const hideSearchResultsDropdownClass = computed(() => {
  if (accountSearchResults.value?.length === 0) {
    return 'collapse';
  }
  return 'visible';
});

const fullAddress = computed(() => {
  let address = '';
  if (!isEmptyString(address1.value, true)) {
    address = `${address}${address1.value} `;
  }
  if (!isEmptyString(city.value, true)) {
    address = `${address}${city.value} `;
  }
  if (!isEmptyString(state.value, true)) {
    address = `${address}${state.value} `;
  }
  if (!isEmptyString(zip.value, true)) {
    address = `${address}${zip.value}`;
  }

  return address;
});

const getValues = (vals: PendingAccount) => {
  if (model?.value) {
    const tmpVals = { ...vals };
    // The default values returned from vee-validate generates a new guid, so we need to make sure to use the original
    // guid from the model so we don't lose track of the object
    delete tmpVals.key;

    return { ...model.value, ...tmpVals };
  }

  return vals;
};

const onSubmit = handleSubmit(vals => emit('on:submit', getValues(vals)));

const getFormValues = () => new Promise<PendingAccount>(resolve => resolve(values));

const onAddressFieldChange = () => emit('on:address-change', fullAddress.value);

const autoRef = ref(null);

const doSearch = (e?: AutoCompleteCompleteEvent) => {
  if (props.useAdvertiserSearch && props.agencyId) {
    getAdvertisersByAgency(props.agencyId, results => {
      if (results?.length) {
        accountSearchResults.value = results;
        autoRef.value?.show();
      } else {
        accountSearchResults.value = [];
      }
    });
  } else if (!isEmptyString(e?.query, true)) {
    searchAccounts({ name: e.query }, results => {
      if (results?.length) {
        accountSearchResults.value = results;
      } else {
        accountSearchResults.value = [];
      }
    });
  }
};

function onInputFocus(evt: Event) {
  doSearch({ originalEvent: evt, query: '' });
}

const formatAddress = (address: Address) => {
  if (!address) return '';
  if (
    isEmptyString(address.address_line1) &&
    isEmptyString(address.city) &&
    isEmptyString(address.region) &&
    isEmptyString(address.postal_code)
  ) {
    return '';
  }
  return `${address.address_line1 ?? ''}, ${address.city ?? ''}, ${address.region ?? ''} ${address.postal_code ?? ''}`;
};

const onAccountSelected = (e: AutoCompleteOptionSelectEvent) => {
  const selection = e.value as AssociationAccount;
  if (!selection?.c2Id) return;

  emit('on:account-selected', transformSearchResult(selection, findUserById(selection.ownerId)));
};

const onCompanyNameChange = () => {
  if (typeof selectedAccount.value === 'string') {
    company.value = selectedAccount.value;
  }
};

const shouldDisableSearchOption = (account: AssociationAccount) => !props.allowedSelectionTypes.includes(account.c2RawType) || !account.isAllowedSelection || (props.disallowAccountsWithAgencies && account.agencyName !== '');

const reset = () => {
  resetForm();
  accountSearchResults.value = [];
  selectedAccount.value = undefined;
};

defineExpose<{ getFormValues: () => Promise<PendingAccount>, reset: () => void }>({ getFormValues, reset });


onMounted(async () => {
  await nextTick();

  const input = autoRef.value?.$el?.querySelector('input');
  input?.focus();
});

</script>
<template>
  <div class="flex flex-col gap-3 w-full">
    <h2 v-if="!props.hideTitle" class="font-medium text-sm text-[var(--swimlane-secondary-text)]">
      {{ props.title ?? 'Search or Create Account' }}
    </h2>
    <Message
      v-if="!canAddMoreAccounts"
      severity="warn"
      icon="pi pi-exclamation-circle"
      class="mb-4"
    >
      You have associated the maximum number of Accounts
    </Message>
    <form @submit="onSubmit">
      <Stack :gap="5">
        <InputGroup>
          <Typography variant="form-label">
            Name
          </Typography>
          <AutoComplete
            v-if="!props.disableAccountSearch"
            ref="autoRef"
            v-model="selectedAccount"
            option-label="c2Name"
            placeholder="Search or enter Account name"
            :delay="300"
            :min-length="props.useAdvertiserSearch && props.agencyId ? 0 : 3"
            :disabled="(!canAddMoreAccounts && !model?.name) || props.isLoading || props.disabled"
            :suggestions="accountSearchResults"
            :overlay-class="hideSearchResultsDropdownClass"
            :option-disabled="shouldDisableSearchOption"
            :invalid="Boolean(errors.name)"
            fluid
            @complete="doSearch"
            @option-select="onAccountSelected"
            @update:model-value="onCompanyNameChange"
            @focus="onInputFocus"
          >
            <template #header>
              <div class="flex items-center bg-[var(--account-search-bg)] p-2">
                <p v-if="!props.useAdvertiserSearch" class="text-xs text-[var(--account-search-color)] font-semibold uppercase ml-3 py-2">
                  Existing Accounts with similar names
                </p>
                <p v-else class="text-xs text-[var(--account-search-color)] font-semibold uppercase ml-3 py-2">
                  Suggested Advertisers
                </p>
              </div>
            </template>
            <template #option="slotProps">
              <div class="flex items-center ml-3 gap-3 text-[var(--account-search-color)]">
                <span class="pi pi-building" aria-label="Building icon" />
                <div class="flex flex-col gap-1">
                  <p class="text-sm font-medium leading-none">
                    {{ `${slotProps.option.c2Name}${slotProps.option.c2Type === 'Agency' ? ' (Agency)' : ''}` }}
                  </p>
                  <p v-if="slotProps.option.addresses?.length > 0" class="text-xs font-normal leading-none">
                    {{ formatAddress(slotProps.option.addresses[0]) }}
                  </p>
                </div>
              </div>
            </template>
            <template #empty>
              <div />
            </template>
          </AutoComplete>
          <InputText
            v-if="props.disableAccountSearch"
            v-model="company"
            placeholder="Account name"
            :disabled="props.disabled"
            fluid
          />
          <Typography v-if="errors.name" variant="form-error">
            {{ errors.name }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Industry
          </Typography>
          <Select
            v-model="industry"
            :options="industryCodes"
            option-label="description"
            filter
            filter-placeholder="Search"
            :loading="isIndustryCodesLoading"
            :placeholder="industryCodePlaceholder"
            :disabled="props.isLoading || props.disabled"
            :invalid="company !== '' && !errors.industry && props.industryCodeRequired"
          />
          <Typography v-if="errors.industry" variant="form-error">
            {{ errors.industry }}
          </Typography>
        </InputGroup>
        <InputGroup v-if="!hideTypeSelection">
          <Typography variant="form-label">
            Account Type
          </Typography>
          <Select
            v-model="type"
            v-bind="typeAttrs"
            :options="allowedCreationTypes"
            :invalid="Boolean(errors.type)"
            placeholder="Select type"
            :disabled="props.isLoading || props.disabled || (disableTypeSelection && !model?.type)"
          />
          <Typography v-if="errors.type" variant="form-error">
            {{ errors.type }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Markets
          </Typography>
          <MultiSelect
            v-model="selectedMarkets"
            :options="markets"
            option-label="market"
            :loading="isMarketsLoading"
            :disabled="props.isLoading || props.disabled"
            :placeholder="marketsPlaceholder"
          />
          <Typography v-if="errors.markets" variant="form-error">
            {{ errors.markets }}
          </Typography>
        </InputGroup>
        <Columns>
          <Column>
            <InputGroup>
              <Typography variant="form-label">
                Phone
              </Typography>
              <IconField icon-position="left">
                <InputIcon class="pi pi-envelope" />
                <InputMask
                  v-model="phone"
                  v-bind="phoneAttrs"
                  mask="999-999-9999"
                  placeholder="555-555-1234"
                  fluid
                  :disabled="props.isLoading || props.disabled"
                />
              </IconField>
              <Typography v-if="errors.phoneNumbers" variant="form-error">
                {{ errors.phoneNumbers }}
              </Typography>
            </InputGroup>
          </Column>
        </Columns>
        <InputGroup>
          <Typography variant="form-label">
            Address
          </Typography>
          <InputText
            v-model="address1"
            v-bind="address1Attrs"
            placeholder="Address line 1"
            :disabled="props.isLoading || props.disabled"
            fluid
            @update:model-value="onAddressFieldChange"
          />
          <Typography v-if="errors.addresses" variant="form-error">
            {{ errors.addresses }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Address Line 2
          </Typography>
          <InputText
            v-model="address2"
            v-bind="address2Attrs"
            placeholder="Address line 2"
            fluid
            :disabled="props.isLoading || props.disabled"
          />
          <Typography v-if="errors.addresses" variant="form-error">
            {{ errors.addresses }}
          </Typography>
        </InputGroup>
        <Columns>
          <Column>
            <InputGroup>
              <Typography variant="form-label">
                City
              </Typography>
              <InputText
                v-model="city"
                v-bind="cityAttrs"
                placeholder="City"
                class="max-w-full"
                :disabled="props.isLoading || props.disabled"
                @update:model-value="onAddressFieldChange"
              />
            </InputGroup>
          </Column>
          <Column>
            <InputGroup>
              <Typography variant="form-label">
                State
              </Typography>
              <InputText
                v-model="state"
                v-bind="stateAttrs"
                placeholder="State"
                class="max-w-full"
                :disabled="props.isLoading || props.disabled"
                @update:model-value="onAddressFieldChange"
              />
            </InputGroup>
          </Column>
          <Column>
            <InputGroup>
              <Typography variant="form-label">
                Postal Code
              </Typography>
              <InputText
                v-model="zip"
                v-bind="zipAttrs"
                placeholder="Zip"
                class="max-w-full"
                :disabled="props.isLoading || props.disabled"
                @update:model-value="onAddressFieldChange"
              />
            </InputGroup>
          </Column>
        </Columns>
        <Toolbar align="left">
          <div>
            <Button
              type="submit"
              :label="submitLabel"
              class="w-full"
              :disabled="disableSubmit"
            />
          </div>
          <div>
            <Button
              v-if="showCancelButton"
              label="Cancel"
              severity="secondary"
              @click="emit('cancel')"
            />
          </div>
        </Toolbar>
      </Stack>
    </form>
  </div>
</template>
